import type { Theme } from "@mui/material";
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AutoAwesome as StardustIcon,
  Campaign as AnnouncementIcon,
  ConfirmationNumber as RaffleIcon,
} from "@mui/icons-material";
import type { NotificationVariant } from "Infrastructure/Api/Api";
import type { ReactNode } from "react";
import { Resources } from "Translations/Resources";
import type { TFunction } from "Translations/Resources";
import type { UserNotificationDto } from "Infrastructure/Api/Api";
import { MS_TIME } from "Utils/TimeUtils";
import i18next from "i18next";

const TRANSLATION_KEY_PREFIX = "@@";

export const resolveTranslatableText = (text: string): string => {
  if (!text.startsWith(TRANSLATION_KEY_PREFIX)) {
    return text;
  }

  const withoutPrefix = text.slice(TRANSLATION_KEY_PREFIX.length);
  const [key, ...paramParts] = withoutPrefix.split("|");
  const params: Record<string, string | number> = {};

  for (const part of paramParts) {
    const eqIndex = part.indexOf("=");
    if (eqIndex > 0) {
      const name = part.slice(0, eqIndex);
      const value = part.slice(eqIndex + 1);
      // i18next picks plural forms only for a numeric `count`; other params stay as sent.
      params[name] =
        name === "count" && /^-?\d+$/.test(value) ? Number(value) : value;
    }
  }

  return i18next.t(key, params);
};

type VariantConfig = {
  icon: ReactNode;
  iconBackgroundColor: string;
  backgroundColor: string;
  seenIndicatorColor: string;
  textColor: string;
  secondaryTextColor: string;
};

export const getVariantConfig = (
  variant: NotificationVariant,
  { palette }: Theme,
): VariantConfig => {
  const configs: Record<NotificationVariant, VariantConfig> = {
    Critical: {
      icon: (
        <WarningIcon
          fontSize="small"
          sx={{ mt: -0.5, color: palette.error.main }}
        />
      ),
      iconBackgroundColor: palette.common.white,
      backgroundColor: palette.error.main,
      seenIndicatorColor: palette.common.white,
      textColor: palette.common.white,
      secondaryTextColor: palette.common.white,
    },
    Error: {
      icon: (
        <WarningIcon
          fontSize="small"
          sx={{ mt: -0.5, color: palette.common.white }}
        />
      ),
      iconBackgroundColor: palette.error.main,
      backgroundColor: palette.common.white,
      seenIndicatorColor: palette.primary.main,
      textColor: palette.text.primary,
      secondaryTextColor: palette.text.secondary,
    },
    Announcement: {
      icon: (
        <AnnouncementIcon
          fontSize="small"
          sx={{ color: palette.common.white }}
        />
      ),
      iconBackgroundColor: palette.info.main,
      backgroundColor: palette.common.white,
      seenIndicatorColor: palette.primary.main,
      textColor: palette.text.primary,
      secondaryTextColor: palette.text.secondary,
    },
    Completed: {
      icon: (
        <CheckCircleIcon
          fontSize="small"
          sx={{ color: palette.common.white }}
        />
      ),
      iconBackgroundColor: palette.success.main,
      backgroundColor: palette.common.white,
      seenIndicatorColor: palette.primary.main,
      textColor: palette.text.primary,
      secondaryTextColor: palette.text.secondary,
    },
    Stardust: {
      icon: (
        <StardustIcon
          fontSize="small"
          sx={{ height: "16px", ml: -0.5, color: palette.common.white }}
        />
      ),
      iconBackgroundColor: palette.primary.main,
      backgroundColor: palette.common.white,
      seenIndicatorColor: palette.primary.main,
      textColor: palette.text.primary,
      secondaryTextColor: palette.text.secondary,
    },
    Raffle: {
      icon: (
        <RaffleIcon
          fontSize="small"
          sx={{ height: "16px", ml: -0.5, color: palette.common.white }}
        />
      ),
      iconBackgroundColor: palette.primary.main,
      backgroundColor: palette.common.white,
      seenIndicatorColor: palette.primary.main,
      textColor: palette.text.primary,
      secondaryTextColor: palette.text.secondary,
    },
  };

  return configs[variant];
};

export const groupNotificationsByAge = (
  notifications: Array<UserNotificationDto>,
  t: TFunction,
): Array<{
  title: string;
  notifications: Array<UserNotificationDto>;
}> => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - MS_TIME.day);

  const todayNotifications = notifications.filter(
    (n) => new Date(n.scheduledDate) >= today,
  );
  const yesterdayNotifications = notifications.filter(
    (n) =>
      new Date(n.scheduledDate) >= yesterday &&
      new Date(n.scheduledDate) < today,
  );
  const olderNotifications = notifications.filter(
    (n) => new Date(n.scheduledDate) < yesterday,
  );

  return [
    {
      title: t(Resources.UserMenu.Notifications.Today),
      notifications: todayNotifications,
    },
    {
      title: t(Resources.UserMenu.Notifications.Yesterday),
      notifications: yesterdayNotifications,
    },
    {
      title: t(Resources.UserMenu.Notifications.Older),
      notifications: olderNotifications,
    },
  ].filter((group) => group.notifications.length > 0);
};
